// src/pages/admin/customers/CustomerGroups.jsx
import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import CreateCustomerGroupModal from '../../../components/modals/CreateCustomerGroupModal'
import EditCustomerGroupModal from '../../../components/modals/EditCustomerGroupModal'
import ViewCustomerGroupModal from '../../../components/modals/ViewCustomerGroupModal'
import { 
  Plus, 
  Search,
  Eye,
  Edit, 
  Trash2, 
  Filter,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Users,
  User,
  X,
  Package
} from 'lucide-react'
import CustomerGroupService from '../../../services/customerGroupService'
import InlineLoader from '../../../components/ui/InlineLoader'
import ButtonSpinner from '../../../components/ui/ButtonSpinner'
import { CustomerGroupModel } from '../../../utils/customerGroupModel'

// Enhanced Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-in slide-in-from-right-full duration-300 ${
    type === 'success' 
      ? 'bg-green-500 text-white' 
      : type === 'error'
      ? 'bg-red-500 text-white'
      : type === 'warning'
      ? 'bg-yellow-500 text-white'
      : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <XCircle className="h-5 w-5" /> : 
     <AlertCircle className="h-5 w-5" />}
    <span className="font-medium">{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2 hover:bg-black hover:bg-opacity-20 rounded p-1">
      <X className="h-4 w-4" />
    </button>
  </div>
)

// Enhanced Stats Card Component
const StatsCard = ({ icon: Icon, title, value, color = "orange" }) => (
  <Card className="hover:shadow-md transition-shadow duration-200">
    <CardContent className="p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
)

export const CustomerGroups = () => {
  const [groups, setGroups] = useState([])
  const [filteredGroups, setFilteredGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [actionLoading, setActionLoading] = useState({})
  const [toast, setToast] = useState(null)
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    totalCustomers: 0
  })

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)

  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = '') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // Enhanced refresh function
  const handleRefresh = async () => {
    console.log('🔄 Manual refresh triggered')
    setLoading(true)
    setError(null)
    
    try {
      const response = await CustomerGroupService.getAllCustomerGroups({ page: 1, limit: 100 })
      
      if (response?.success) {
        const groupsData = safeGet(response, 'data.groups', []) || safeGet(response, 'data.customerGroups', [])
        console.log('✅ Refreshed', groupsData.length, 'customer groups')
        
        const validGroups = Array.isArray(groupsData) 
          ? groupsData.filter(group => group && group._id)
          : []
        
        setGroups(validGroups)
        
        const calculatedStats = CustomerGroupModel.calculateGroupStats(validGroups)
        setStats(calculatedStats)
      }
      
      showToast('Data refreshed successfully', 'success')
    } catch (error) {
      console.error('❌ Error during refresh:', error)
      setError(error.message || 'Failed to refresh data')
      showToast('Error refreshing data', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Fetch customer groups
  const fetchCustomerGroups = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('👥 Fetching customer groups...')

      const response = await CustomerGroupService.getAllCustomerGroups({ page: 1, limit: 100 })
      
      if (response?.success) {
        const groupsData = safeGet(response, 'data.groups', []) || safeGet(response, 'data.customerGroups', [])
        console.log('✅ Fetched', groupsData.length, 'customer groups')
        
        const validGroups = Array.isArray(groupsData) 
          ? groupsData.filter(group => group && group._id)
          : []
        
        setGroups(validGroups)
        
        const calculatedStats = CustomerGroupModel.calculateGroupStats(validGroups)
        setStats(calculatedStats)
        
      } else {
        console.warn('Customer groups API request unsuccessful:', response)
        setError('Failed to fetch customer groups')
      }
      
    } catch (error) {
      console.error('❌ Error fetching customer groups:', error)
      setError(error.message || 'Failed to fetch customer groups')
    } finally {
      setLoading(false)
    }
  }

  // Filter groups based on search and filters
  const applyFilters = () => {
    let filtered = [...groups]
    
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(group => 
        group.name?.toLowerCase().includes(searchLower) ||
        group.description?.toLowerCase().includes(searchLower)
      )
    }
    
    if (statusFilter) {
      const isActive = statusFilter === 'active'
      filtered = filtered.filter(group => group.isActive === isActive)
    }
    
    setFilteredGroups(filtered)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('')
  }

  // Load data on component mount
  useEffect(() => {
    fetchCustomerGroups()
  }, [])

  // Apply filters when dependencies change
  useEffect(() => {
    applyFilters()
  }, [groups, searchTerm, statusFilter])

  // Handle group actions
  const handleViewGroup = (group) => {
    setSelectedGroup(group)
    setShowViewModal(true)
  }

  const handleEditGroup = (group) => {
    setSelectedGroup(group)
    setShowEditModal(true)
  }

  // Handle group events
  const handleGroupCreated = (newGroup) => {
    console.log('👥 New customer group created:', newGroup)
    showToast('Customer group created successfully!', 'success')
    fetchCustomerGroups()
  }

  const handleGroupUpdated = (updatedGroup) => {
    console.log('👥 Customer group updated:', updatedGroup)
    showToast('Customer group updated successfully!', 'success')
    fetchCustomerGroups()
  }

  // Toggle group status
  const handleToggleStatus = async (group) => {
    const groupId = group._id
    try {
      setActionLoading(prev => ({ ...prev, [groupId]: true }))
      
      const newStatus = !group.isActive
      await CustomerGroupService.updateCustomerGroupStatus(groupId, newStatus)
      
      showToast(
        `Customer group ${newStatus ? 'activated' : 'deactivated'} successfully!`, 
        'success'
      )
      fetchCustomerGroups()
    } catch (error) {
      console.error('❌ Error updating group status:', error)
      showToast('Failed to update group status', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [groupId]: false }))
    }
  }

  if (loading) {
    return (
      <InlineLoader text="Loading customer groups..." />
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {/* Enhanced Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customer Groups</h1>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="flex items-center space-x-2 border-gray-300 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Group
          </Button>
        </div>
      </div>

      {/* Enhanced Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          icon={Users}
          title="Total Groups"
          value={stats.total.toLocaleString()}
          color="blue"
        />
        <StatsCard
          icon={CheckCircle}
          title="Active Groups"
          value={stats.active.toLocaleString()}
          color="green"
        />
        <StatsCard
          icon={XCircle}
          title="Inactive Groups"
          value={stats.inactive.toLocaleString()}
          color="red"
        />
        <StatsCard
          icon={User}
          title="Total Customers"
          value={stats.totalCustomers.toLocaleString()}
          color="purple"
        />
      </div>

      {/* Enhanced Filters Card */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Groups</h2>
            </div>
            {(searchTerm || statusFilter) && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
                size="sm"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          
          {/* Results Count */}
          <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
            Showing <span className="font-medium text-gray-900">{filteredGroups.length}</span> of <span className="font-medium text-gray-900">{groups.length}</span> groups
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Group List */}
      <Card className="shadow-sm">
        <CardHeader className="border-b border-gray-200">
          <CardTitle className="flex items-center space-x-2">
            <Package className="h-5 w-5 text-orange-600" />
            <span>Groups Directory</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {error ? (
            <div className="text-center py-12 px-6">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Groups</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button 
                onClick={handleRefresh} 
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="text-center py-12 px-6">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {groups.length === 0 ? 'No customer groups found' : 'No groups match your filters'}
              </h3>
              <p className="text-gray-600 mb-4">
                {groups.length === 0 
                  ? 'Get started by creating your first customer group.' 
                  : 'Try adjusting your search criteria.'}
              </p>
              {groups.length === 0 && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Group
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <table className="w-full min-w-full bg-white">
                  {/* Table Header */}
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Group Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customers
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  {/* Table Body */}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredGroups.map((group) => {
                      const customerCount = CustomerGroupModel.getCustomerCount(group)
                      const formattedGroup = CustomerGroupService.formatCustomerGroupForDisplay(group)
                      
                      return (
                        <tr key={group._id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <Users className="h-4 w-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{group.name}</p>
                                <p className="text-sm text-gray-500">ID: {group._id.slice(-8)}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="max-w-xs">
                              <p className="text-gray-900 truncate">
                                {group.description || 'No description'}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900 font-medium">{customerCount}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleToggleStatus(group)}
                              disabled={actionLoading[group._id]}
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${
                                group.isActive 
                                  ? 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200'
                              } ${
                                actionLoading[group._id] 
                                  ? 'opacity-50 cursor-not-allowed' 
                                  : 'cursor-pointer hover:opacity-80'
                              }`}
                            >
                              {actionLoading[group._id] && (
                                <ButtonSpinner />
                              )}
                              {/* {group.isActive ? (
                                <CheckCircle className="h-3 w-3 mr-1" />
                              ) : (
                                <XCircle className="h-3 w-3 mr-1" />
                              )} */}
                              {group.isActive ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                onClick={() => handleViewGroup(group)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                title="View group"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEditGroup(group)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                title="Edit group"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4 p-4">
                {filteredGroups.map((group) => {
                  const customerCount = CustomerGroupModel.getCustomerCount(group)
                  
                  return (
                    <Card key={group._id} className="shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {/* Group Header */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <Users className="h-4 w-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{group.name}</p>
                                <p className="text-sm text-gray-500">ID: {group._id.slice(-8)}</p>
                              </div>
                            </div>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                              group.isActive 
                                ? 'bg-green-100 text-green-800 border-green-200' 
                                : 'bg-gray-100 text-gray-800 border-gray-200'
                            }`}>
                              {group.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>

                          {/* Group Details */}
                          <div className="space-y-2">
                            <div>
                              <p className="text-sm text-gray-500">Description</p>
                              <p className="text-gray-900">{group.description || 'No description'}</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900 font-medium">{customerCount} customers</span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-100">
                            <button
                              onClick={() => handleViewGroup(group)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                              title="View group"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditGroup(group)}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                              title="Edit group"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <CreateCustomerGroupModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onGroupCreated={handleGroupCreated}
      />

      <ViewCustomerGroupModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        group={selectedGroup}
      />

      <EditCustomerGroupModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        group={selectedGroup}
        onGroupUpdated={handleGroupUpdated}
      />
    </div>
  )
}